import { useState, useEffect, useCallback } from 'react';
import { Box } from '../../../components/Box';
import { Modal } from '../../../components/Modal';
import { Colors } from '../../../values/colors';
import { ErrorMessage } from '../../../utils/alerts';
import { getServiceOrders } from '../../../services/Fiscal';
import {
  PageWrapper, FiltersRow, FilterGroup, FilterLabel, FilterInput, FilterSelect,
  TableWrapper, StyledTable, Th, Td, StatusBadge, ActionBtn,
  DetailGrid, DetailItem, DetailLabel, DetailValue,
  HistoryList, HistoryItem, HistoryTime, HistoryEvent,
} from '../styles';

const STATUS_LABEL = {
  pending: 'Pendente',
  processing: 'Processando',
  created: 'Criada',
  created_warning: 'Criada com alerta',
  error: 'Erro',
};

const STATUS_COLOR = {
  pending: 'yellow',
  processing: 'primaryBlue',
  created: 'successGreen',
  created_warning: 'orange',
  error: 'red',
};

const BILLING_TYPE_LABEL = {
  per_life: 'Por vida',
  per_life_with_minimum: 'Por vida + valor mínimo',
  fixed: 'Valor fixo',
  tiers: 'Por faixa',
  tiers_with_minimum: 'Por faixa com valor mínimo',
};

const fmt = (v) =>
  v != null ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

const fmtRate = (v) =>
  `${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const ValueBreakdown = ({ item }) => {
  const base = Number(item.base_amount || 0);
  const tele = Number(item.telemedicine_amount || 0);
  const teleo = Number(item.teleorientation_amount || 0);
  const users = Number(item.eligible_users || 0);

  const minimumApplied = Boolean(Number(item.minimum_applied));
  const perLife = users > 0 ? base / users : 0;
  const parcela = { fontSize: 11, fontWeight: 400, color: Colors.mutedGray, lineHeight: 1.4, whiteSpace: 'normal' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', whiteSpace: 'normal' }}>
      <span>{fmt(item.amount)}</span>
      {minimumApplied ? (
        <span style={{ ...parcela, color: '#b07800' }}>
          valor mínimo aplicado{item.minimum_amount != null ? ` (${fmt(item.minimum_amount)})` : ''}
        </span>
      ) : (
        <>
          <span style={parcela}>
            Base: {fmt(base)}{users > 0 ? ` (${users} × ${fmt(perLife)})` : ''}
          </span>
          {tele > 0 && (
            <span style={parcela}>
              Telemed. ({fmtRate(item.telemedicine_rate)}): {fmt(tele)}
              {users > 0 ? ` (${users} × ${fmt(tele / users)})` : ''}
            </span>
          )}
          {teleo > 0 && (
            <span style={parcela}>
              Teleorient. ({fmtRate(item.teleorientation_rate)}): {fmt(teleo)}
              {users > 0 ? ` (${users} × ${fmt(teleo / users)})` : ''}
            </span>
          )}
        </>
      )}
    </div>
  );
};

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('pt-BR');
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString('pt-BR');
};

const parseHistory = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  try { return JSON.parse(raw); } catch { return []; }
};

const parseOmieResponse = (raw) => {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return null; }
};

const omieResponseMessage = (resp) =>
  resp?.faultstring || resp?.cDescStatus || resp?.descricao_status || null;

const PER_PAGE = 10;

export const FiscalServiceOrders = () => {
  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]         = useState('');
  const [status, setStatus]         = useState('');
  const [dateFrom, setDateFrom]     = useState('');
  const [dateTo, setDateTo]         = useState('');
  const [detail, setDetail]         = useState(null);
  const [itemsExpanded, setItemsExpanded] = useState(false);
  const [page, setPage]             = useState(1);

  const openDetail = (order) => {
    setItemsExpanded(false);
    setDetail(order);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceOrders({ search, status, dateFrom, dateTo });
      setOrders(data.list || []);
      setPage(1);
    } catch (e) {
      ErrorMessage({ message: 'Erro ao carregar ordens de serviço.' });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [search, status, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil(orders.length / PER_PAGE));
  const paged = orders.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const handleSearch = () => setSearch(searchInput);

  return (
    <PageWrapper>
      <FiltersRow>
        <FilterGroup>
          <FilterLabel>Buscar empresa</FilterLabel>
          <FilterInput
            placeholder="Nome ou ID..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
        </FilterGroup>
        <ActionBtn style={{ alignSelf: 'flex-end' }} onClick={handleSearch}>Buscar</ActionBtn>
        <FilterGroup>
          <FilterLabel>Solicitação — de</FilterLabel>
          <FilterInput type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} width="150px" />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Solicitação — até</FilterLabel>
          <FilterInput type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} width="150px" />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Status</FilterLabel>
          <FilterSelect value={status} onChange={e => setStatus(e.target.value)} width="170px">
            <option value="">Todos</option>
            <option value="pending">Pendente</option>
            <option value="processing">Processando</option>
            <option value="created">Criada</option>
            <option value="created_warning">Criada com alerta</option>
            <option value="error">Erro</option>
          </FilterSelect>
        </FilterGroup>
      </FiltersRow>

      <Box noPadding noHeader>
        <TableWrapper>
          <StyledTable style={{ minWidth: 1130 }}>
            <thead>
              <tr>
                <Th w="60px">ID</Th>
                <Th w="200px">Empresa faturadora</Th>
                <Th w="155px" center>Período</Th>
                <Th w="70px" center>Itens</Th>
                <Th w="80px" right>Vidas</Th>
                <Th w="115px" right>Valor faturado</Th>
                <Th w="130px">Solicitado em</Th>
                <Th w="100px" center>Status</Th>
                <Th w="120px" center>Nº OS</Th>
                <Th w="100px" center>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><Td colSpan={10} style={{ textAlign: 'center', color: Colors.mutedGray }}>Carregando...</Td></tr>
              )}
              {!loading && paged.map(o => (
                <tr key={o.id}>
                  <Td muted w="60px">{o.company_id}</Td>
                  <Td ellipsis w="200px" title={o.company || undefined}>{o.company}</Td>
                  <Td center muted w="155px" style={{ fontSize: 12 }}>
                    {fmtDate(o.period_start)} — {fmtDate(o.period_end)}
                  </Td>
                  <Td center muted w="70px">{o.items_count ?? (o.items ? o.items.length : 0)}</Td>
                  <Td right w="80px">{Number(o.total_eligible_users || 0).toLocaleString('pt-BR')}</Td>
                  <Td right bold w="115px" title={Number(o.group_minimum_applied) ? `Mínimo de grupo aplicado (soma dos itens: ${fmt(o.total_amount)})` : undefined}>
                    {fmt(o.billed_amount ?? o.total_amount)}
                    {Number(o.group_minimum_applied) ? <span style={{ color: '#b07800' }}> *</span> : null}
                  </Td>
                  <Td muted w="130px" style={{ fontSize: 12 }}>{fmtDateTime(o.requested_at)}</Td>
                  <Td center w="100px">
                    <StatusBadge
                      color={STATUS_COLOR[o.status]}
                      title={o.status === 'created_warning' && o.warning ? o.warning : undefined}
                    >
                      {STATUS_LABEL[o.status] || o.status}
                    </StatusBadge>
                  </Td>
                  <Td center muted ellipsis w="120px" title={o.os_number || undefined}>
                    {o.os_number || '—'}
                  </Td>
                  <Td center w="100px">
                    <ActionBtn small variant="outline" onClick={() => openDetail(o)}>Detalhes</ActionBtn>
                  </Td>
                </tr>
              ))}
              {!loading && orders.length === 0 && (
                <tr><Td colSpan={10} style={{ textAlign: 'center', color: Colors.mutedGray }}>Nenhuma ordem de serviço encontrada.</Td></tr>
              )}
            </tbody>
          </StyledTable>
        </TableWrapper>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${Colors.ultraLightGray2}` }}>
          <span style={{ fontSize: 13, color: Colors.mutedGray }}>
            {orders.length} solicitaç{orders.length !== 1 ? 'ões' : 'ão'}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: Colors.mutedGray }}>Página {page} de {pages}</span>
            <ActionBtn small onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</ActionBtn>
            <ActionBtn small onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>›</ActionBtn>
          </div>
        </div>
      </Box>

      {/* Modal de detalhes */}
      <Modal
        open={!!detail}
        title="Detalhes da Ordem de Serviço"
        onClose={() => setDetail(null)}
        containerStyle={{ width: '100%', maxWidth: 860, marginTop: 20, marginBottom: 20 }}
      >
        {detail && (
          <>
            <DetailGrid>
              <DetailItem><DetailLabel>Empresa faturadora</DetailLabel><DetailValue>{detail.company}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>CNPJ</DetailLabel><DetailValue>{detail.cnpj || '—'}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>Período</DetailLabel><DetailValue>{fmtDate(detail.period_start)} — {fmtDate(detail.period_end)}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>Total de vidas</DetailLabel><DetailValue>{Number(detail.total_eligible_users || 0).toLocaleString('pt-BR')}</DetailValue></DetailItem>
              <DetailItem><DetailLabel>Empresas cobradas</DetailLabel><DetailValue>{detail.items_count ?? (detail.items ? detail.items.length : 0)}</DetailValue></DetailItem>
              {Number(detail.group_minimum_applied) ? (
                <>
                  <DetailItem><DetailLabel>Soma dos itens</DetailLabel><DetailValue>{fmt(detail.total_amount)}</DetailValue></DetailItem>
                  <DetailItem><DetailLabel>Mínimo de grupo</DetailLabel><DetailValue style={{ color: '#b07800' }}>{fmt(detail.group_minimum_amount)}</DetailValue></DetailItem>
                  <DetailItem><DetailLabel>Valor faturado</DetailLabel><DetailValue bold>{fmt(detail.billed_amount ?? detail.total_amount)}</DetailValue></DetailItem>
                </>
              ) : (
                <DetailItem><DetailLabel>Valor total faturado</DetailLabel><DetailValue bold>{fmt(detail.billed_amount ?? detail.total_amount)}</DetailValue></DetailItem>
              )}
              <DetailItem>
                <DetailLabel>Status</DetailLabel>
                <DetailValue>
                  <StatusBadge color={STATUS_COLOR[detail.status]}>{STATUS_LABEL[detail.status] || detail.status}</StatusBadge>
                </DetailValue>
              </DetailItem>
              <DetailItem><DetailLabel>Número da OS</DetailLabel><DetailValue bold>{detail.os_number || '—'}</DetailValue></DetailItem>
            </DetailGrid>

            <button
              type="button"
              onClick={() => setItemsExpanded(v => !v)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                margin: '4px 0 12px', textAlign: 'left',
                fontWeight: 700, fontSize: 13, color: Colors.blueGray,
                textTransform: 'uppercase', letterSpacing: '0.4px',
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1, color: Colors.primaryBlue }}>{itemsExpanded ? '\u25BE' : '\u25B8'}</span>
              Detalhamento por empresa
              <span style={{ fontWeight: 500, color: Colors.mutedGray, textTransform: 'none', letterSpacing: 0 }}>
                ({(detail.items ? detail.items.length : 0).toLocaleString('pt-BR')})
              </span>
            </button>
            {itemsExpanded && (
              <TableWrapper style={{ marginBottom: 20 }}>
                <StyledTable style={{ minWidth: 770 }}>
                  <thead>
                    <tr>
                      <Th w="200px">Empresa</Th>
                      <Th w="140px">CNPJ</Th>
                      <Th w="110px">Modelo</Th>
                      <Th w="70px" right>Vidas</Th>
                      <Th w="250px" right>Valor</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail.items || []).map((it) => (
                      <tr key={it.business_id}>
                        <Td ellipsis w="200px" title={it.company_title || undefined}>{it.company_title}</Td>
                        <Td muted w="140px">{it.cnpj || '—'}</Td>
                        <Td muted w="110px">{BILLING_TYPE_LABEL[it.billing_type] || it.billing_type || '—'}</Td>
                        <Td right w="70px">{Number(it.eligible_users || 0).toLocaleString('pt-BR')}</Td>
                        <Td right bold w="250px"><ValueBreakdown item={it} /></Td>
                      </tr>
                    ))}
                    {(!detail.items || detail.items.length === 0) && (
                      <tr><Td colSpan={5} style={{ textAlign: 'center', color: Colors.mutedGray }}>Sem itens.</Td></tr>
                    )}
                  </tbody>
                </StyledTable>
              </TableWrapper>
            )}

            {(() => {
              const isError = detail.status === 'error';
              const omieResp = parseOmieResponse(detail.omie_response);
              const omieMsg = omieResponseMessage(omieResp);
              if (!detail.warning && !omieMsg && !omieResp) return null;
              const tone = isError ? Colors.red : Colors.orange;
              return (
                <div style={{
                  background: tone + '14', border: `1px solid ${tone}55`,
                  borderRadius: 8, padding: '12px 14px', marginBottom: 16,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 16, lineHeight: 1 }}>&#9888;</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: tone, fontWeight: 600, lineHeight: 1.5 }}>
                        {isError ? 'Falha ao criar a Ordem de Serviço' : 'Atenção'}
                      </div>
                      {detail.warning && (
                        <div style={{ fontSize: 13, color: Colors.blueGray, marginTop: 4, lineHeight: 1.5 }}>
                          {detail.warning}
                        </div>
                      )}
                      {omieMsg && omieMsg !== detail.warning && (
                        <div style={{ fontSize: 13, color: Colors.blueGray, marginTop: 4, lineHeight: 1.5 }}>
                          <strong>Retorno da Omie:</strong> {omieMsg}
                        </div>
                      )}
                      {(omieResp?.faultcode || omieResp?.cCodStatus) && (
                        <div style={{ fontSize: 12, color: Colors.mutedGray, marginTop: 4 }}>
                          {omieResp.faultcode ? `Código: ${omieResp.faultcode}` : `Status: ${omieResp.cCodStatus}`}
                        </div>
                      )}
                      {omieResp && (
                        <details style={{ marginTop: 8 }}>
                          <summary style={{ fontSize: 12, color: Colors.mutedGray, cursor: 'pointer' }}>
                            Ver resposta completa da Omie
                          </summary>
                          <pre style={{
                            fontSize: 11, color: Colors.blueGray, background: Colors.ultraLightGray3,
                            borderRadius: 6, padding: 10, marginTop: 6, overflowX: 'auto', whiteSpace: 'pre-wrap',
                          }}>
                            {JSON.stringify(omieResp, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            <div style={{ fontWeight: 700, fontSize: 13, color: Colors.blueGray, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Histórico
            </div>
            <HistoryList>
              {parseHistory(detail.history).map((h, i) => (
                <HistoryItem key={i} error={String(h.event || '').toLowerCase().includes('erro')}>
                  <HistoryTime>{h.time}</HistoryTime>
                  <HistoryEvent>{h.event}</HistoryEvent>
                </HistoryItem>
              ))}
              {parseHistory(detail.history).length === 0 && (
                <HistoryEvent style={{ color: Colors.mutedGray }}>Sem histórico.</HistoryEvent>
              )}
            </HistoryList>
          </>
        )}
      </Modal>
    </PageWrapper>
  );
};
